import { type SagaIterator } from 'redux-saga'
import { put, takeEvery, call, select, delay, type SagaReturnType } from 'redux-saga/effects'
import { doc, setDoc } from 'firebase/firestore'
import {
    setCurrentRider,
    getCurrentRiderFullProfile,
    setRiderRideHistory,
    toggleEndRideModal,
    setAccountSharingInfo,
    setDiscountGroups,
    getRiderDiscount as getRiderDiscountAction,
    setRiderDiscount,
    setRideParkingPhoto,
    setDriversLicenseStatus,
    getDriversLicenseStatus as getDriversLicenseStatusAction,
    setActiveRideRider,
    setGuestRides,
    setCurrentRideHistoryItem,
    setRiderVoucherDetails as setRiderVoucherDetailsAction,
    getRiderVoucherDetails as getRiderVoucherDetailsAction,
    getAccountSharingInfo as getAccountSharingInfoAction,
    setRiderFineHistory,
    setRiderPaymentDetails,
    setDeleteUserFailed,
    setRiderContracts,
    getRiderContracts as getRiderContractsAction,
    setRiderBillingProfiles,
    setRiderRideHistoryPagination,
} from 'src/redux/rider/rider.actions'
import {
    selectCurrentRider,
    selectRideHistoryDateFilter,
    selectCurrentRideHistory,
    selectCurrentRiderDiscount,
} from 'src/redux/rider/rider.selectors'
import {
    GET_CURRENT_RIDER_FULL_PROFILE,
    GET_ACCOUNT_SHARING_INFO,
    GET_DISCOUNT_GROUPS,
    GET_RIDER_DISCOUNT,
    GIVE_RIDER_CREDITS,
    GIVE_RIDER_DISCOUNTS,
    FORGIVE_RIDER_DEBT,
    EDIT_RIDER_DISCOUNT,
    GET_RIDER_RIDE_HISTORY,
    GET_RIDE_PARKING_PHOTO,
    SUBMIT_END_RIDE,
    BLOCK_RIDER,
    DELETE_RIDER,
    GET_DRIVERS_LICENSE_STATUS,
    DELETE_DRIVERS_LICENSE,
    APPROVE_DRIVERS_LICENSE_DOCUMENT,
    APPROVE_DRIVERS_LICENSE_FACE_CHECK,
    GET_RIDER_CONTRACTS,
    ABANDON_CONTRACT,
    CANCEL_AND_REFUND_CONTRACT,
    STOP_CONTRACT_RENEWAL,
    GET_RIDE_HISTORY_ITEM,
    GET_RIDER_VOUCHER_DETAILS,
    DELETE_RIDER_VOUCHERS,
    UNBLOCK_RIDER_DEVICE_SWITCH,
    UPDATE_DATE_OF_BIRTH,
    DELETE_RIDER_PHONE,
    GET_RIDER_FINE_HISTORY,
    GET_RIDER_BILLING_PROFILES,
    GET_RIDER_PAYMENT_DETAILS,
    REQUEST_ID_CHECK,
    type GetCurrentRiderFullProfile,
    type GetAccountSharingInfo,
    type GetRiderDiscount,
    type GiveRiderCredits,
    type GiveRiderDiscounts,
    type ForgiveRiderDebt,
    type EditRiderDiscount,
    type GetRiderRideHistory,
    type GetRideHistoryItem,
    type GetRideParkingPhoto,
    type SubmitEndRide,
    type BlockRider,
    type DeleteRider,
    type GetDriversLicenseStatus,
    type DeleteDriversLicense,
    type ApproveDriversLicenseDocument,
    type ApproveDriversLicenseFaceCheck,
    type AbandonContract,
    type CancelAndRefundContract,
    type StopContractRenewal,
    type GetRiderVoucherDetails,
    type DeleteRiderVouchers,
    type UnblockRiderDeviceSwitch,
    type UpdateDateOfBirth,
    type ParkingPhoto,
    type DeleteRiderPhone,
    type GetRiderFineHistory,
    type GetRiderBillingProfiles,
    type GetRiderPaymentDetails,
    type RequestIDCheck,
    type GetRiderContracts,
} from 'src/redux/rider/rider.types'
import { createNote } from 'src/redux/note'
import { getRides } from 'src/redux/activeRides'
import {
    giveRiderCredit,
    giveRiderDiscount,
    forgiveUserDebt,
    blockUser,
    unblockUser,
    deleteUser,
    getRider,
    getAccountSharingInfo,
    getDiscountGroups,
    getRiderDiscount,
    removeRiderDiscount,
    updateRiderDiscount,
    addRiderDiscount,
    blockUserFM,
    unblockUserFM,
    getRideParkingPhoto,
    endRide,
    getActiveRideUser,
    getRideHistoryUser,
    getDriversLicenseStatus,
    approveDriversLicenseDocument,
    approveDriversLicenseFaceCheck,
    deleteDriversLicense,
    abandonContract,
    cancelAndRefundContract,
    stopContractRenewal,
    getRideHistoryById,
    getRiderVoucherHistory,
    deleteRiderVouchers as deleteRiderVouchersRequest,
    updateDateOfBirth,
    unblockDeviceSwitch,
    unlinkUserPhone,
    getRiderFineHistoryById,
    getRiderPaymentDetails,
    requestUserDocument,
    getRiderContracts,
    getRiderBillingProfiles,
} from 'src/api'
import { db } from 'src/firebase/firebase'
import { generateUUID } from 'src/utils/uuid/uuidUtils'
import moment from 'moment'
import { showNotification } from 'src/components/parts/notifications/notifications'
import { type RideHistoryItem } from 'src/api/fm/rentals/rentals.model'
import { type DriversLicenseStatus } from 'src/api/fm/users/users.model'
import { type BillingProfile, type Contract, type RiderPaymentDetails } from 'src/api/fm/payments/payments.model'
import * as routes from 'src/constants/routes'
import { isAxiosError } from 'axios'

type GetRiderRes = SagaReturnType<typeof getRider>
type GetActiveRideUserRes = SagaReturnType<typeof getActiveRideUser>

export function* fetchCurrentRider(action: GetCurrentRiderFullProfile) {
    try {
        const id = action.payload
        const res: GetRiderRes = yield call(getRider, id)
        if (res instanceof Error) {
            throw res
        }
        // Set current rider
        yield put(setCurrentRider(res))

        // Set active ride
        const activeRides: GetActiveRideUserRes = yield call(getActiveRideUser, id)

        if (activeRides?.data) {
            yield put(setActiveRideRider(activeRides.data.data))
        }

        // set guest rides or clear it
        if (activeRides?.data.guest_rentals) {
            yield put(setGuestRides(activeRides.data.guest_rentals))
        } else {
            yield put(setGuestRides(null))
        }
    } catch (_) {
        yield put(setCurrentRider({ error: 'Rider not found' }))
    }
}

type GetAccountSharingInfoRes = SagaReturnType<typeof getAccountSharingInfo>

export function* fetchAccountSharingInfo(action: GetAccountSharingInfo): SagaIterator {
    const id = action.payload

    const accountSharingInfo: GetAccountSharingInfoRes = yield call(getAccountSharingInfo, id)

    if (accountSharingInfo instanceof Error) {
        yield call(showNotification, 'error', accountSharingInfo.message)
        return
    }

    yield put(setAccountSharingInfo(accountSharingInfo))
}

type GetDiscountGroupsRes = SagaReturnType<typeof getDiscountGroups>

export function* fetchDiscountGroups() {
    const discountGroups: GetDiscountGroupsRes = yield call(getDiscountGroups)

    if (discountGroups instanceof Error) {
        yield call(showNotification, 'error', discountGroups.message)
        return
    }

    yield put(setDiscountGroups(discountGroups))
}

type GetRiderDiscountRes = SagaReturnType<typeof getRiderDiscount>

export function* fetchRiderDiscount(action: GetRiderDiscount) {
    const id = action.payload
    const riderDiscount: GetRiderDiscountRes = yield call(getRiderDiscount, id)

    if (riderDiscount instanceof Error) {
        yield call(showNotification, 'error', riderDiscount.message)
        return
    }

    yield put(setRiderDiscount(riderDiscount))
}

export function* fetchRiderContracts(action: GetRiderContracts) {
    const riderId = action.payload
    const contracts: Contract[] = yield call(getRiderContracts, riderId)
    if (contracts instanceof Error) {
        yield call(showNotification, 'error', contracts.message)
        return
    }
    yield put(setRiderContracts(contracts))
}

export function* fetchRiderPaymentDetails(action: GetRiderPaymentDetails) {
    const riderId = action.payload
    const paymentDetails: RiderPaymentDetails = yield call(getRiderPaymentDetails, riderId)

    if (paymentDetails instanceof Error) {
        yield call(showNotification, 'error', paymentDetails.message)
        return
    }

    yield put(setRiderPaymentDetails(paymentDetails))
}

export function* fetchRiderBillingProfiles(action: GetRiderBillingProfiles) {
    const riderId = action.payload
    const billingProfiles: BillingProfile[] = yield call(getRiderBillingProfiles, riderId)

    if (billingProfiles instanceof Error) {
        yield call(showNotification, 'error', billingProfiles.message)
        return
    }

    yield put(setRiderBillingProfiles(billingProfiles))
}

type GiveRiderCreditRes = SagaReturnType<typeof giveRiderCredit>

export function* giveCredits(action: GiveRiderCredits) {
    try {
        const res: GiveRiderCreditRes = yield call(giveRiderCredit, action.payload.riderId, action.payload.amount)
        if (res instanceof Error) {
            throw res
        }

        // Add a predefined note
        yield put(createNote(action.payload.note))
        yield put(getCurrentRiderFullProfile(action.payload.riderId))
        yield call(showNotification, 'success', `Successfully added ${action.payload.amount} credits and a note`)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type GiveRiderDiscountsRes = SagaReturnType<typeof giveRiderDiscount>

function* giveDiscounts(action: GiveRiderDiscounts) {
    try {
        const res: GiveRiderDiscountsRes = yield call(
            giveRiderDiscount,
            action.payload.riderId,
            action.payload.name,
            // action.payload.description,
            action.payload.internalName,
            action.payload.expiresAt,
            action.payload.amount,
            action.payload.credits,
            action.payload.unlockFeeDiscountPercent,
            action.payload.rideFeeDiscountPercent,
            action.payload.rideFeeDiscountMinutes,
        )
        if (res instanceof Error) {
            throw res
        }

        // Add a predefined note
        yield put(createNote(action.payload.note))
        yield put(getCurrentRiderFullProfile(action.payload.riderId))
        yield call(showNotification, 'success', `Successfully added discount and note`)
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* forgiveDebt(action: ForgiveRiderDebt) {
    try {
        const { riderId, message } = action.payload
        yield call(forgiveUserDebt, riderId, message)
        yield put(getCurrentRiderFullProfile(riderId))
        yield call(showNotification, 'success', 'Successfully deleted rider debt')
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export function* editRiderDiscount(action: EditRiderDiscount): SagaIterator {
    const { riderId, discountGroupId, validTo } = action.payload
    const riderDiscount: Record<string, any> | null = yield select(selectCurrentRiderDiscount)
    const hasPrevDiscount = !!riderDiscount
    const hasNextDiscount = !!discountGroupId
    let result

    if (!hasNextDiscount) {
        result = yield call(removeRiderDiscount, riderDiscount?.id)
    } else if (hasPrevDiscount) {
        const data = {
            id: riderDiscount.id,
            group_id: discountGroupId,
            user_id: riderId,
            valid_from: 0,
            valid_to: validTo,
        }

        result = yield call(updateRiderDiscount, data)
    } else {
        const data = {
            group_id: discountGroupId,
            user_id: riderId,
            valid_from: 0,
            valid_to: validTo,
        }

        result = yield call(addRiderDiscount, data)
    }

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully updated discount')
    yield put(getRiderDiscountAction(riderId))
}

type GetRideHistoryUserRes = SagaReturnType<typeof getRideHistoryUser>

export function* fetchRideHistory({ payload: cursor }: GetRiderRideHistory) {
    try {
        const rider: Record<string, any> | null = yield select(selectCurrentRider)
        const date: string | undefined = yield select(selectRideHistoryDateFilter) ?? undefined

        const res: GetRideHistoryUserRes = yield call(getRideHistoryUser, rider?.userInfo.id, { date, cursor })
        if (res instanceof Error) {
            throw res
        }

        let updatedHistory
        if (cursor !== '') {
            const currentHistory: RideHistoryItem[] | undefined = yield select(selectCurrentRideHistory)
            updatedHistory = currentHistory ? currentHistory.concat(res.rentals) : res.rentals
        } else {
            updatedHistory = res.rentals
        }
        yield put(setRiderRideHistoryPagination(res.hasMore, res.nextCursor))
        yield put(setRiderRideHistory(updatedHistory))
    } catch (e) {
        console.log('Failed to get ride history', e)
    }
}

type GetRideHistoryByIdRes = SagaReturnType<typeof getRideHistoryById>

export function* fetchRideHistoryItem(action: GetRideHistoryItem) {
    const rideId = action.payload
    const ride: GetRideHistoryByIdRes = yield call(getRideHistoryById, rideId)

    if (ride instanceof Error) {
        yield call(showNotification, 'error', ride.message)
        return
    }

    yield put(setCurrentRideHistoryItem(ride))
}

export function* fetchRideParkingPhoto(action: GetRideParkingPhoto) {
    const rideId = action.payload
    const rideParkingPhoto: ParkingPhoto = yield call(getRideParkingPhoto, rideId)

    if (rideParkingPhoto instanceof Error) {
        yield call(showNotification, 'error', rideParkingPhoto.message)
        return
    }

    yield put(setRideParkingPhoto(rideParkingPhoto))
}

type EndRideRes = SagaReturnType<typeof endRide>

function* endActiveRide(action: SubmitEndRide) {
    try {
        const { user, ride, data } = action.payload

        const res: EndRideRes = yield call(endRide, {
            rental_id: ride?.rental_id!,
            newDuration: data.minutes,
            source: ride?.source!,
        })

        if (res instanceof Error) {
            throw res
        }

        if (data.note.title && data.note.desc) {
            const id = generateUUID()
            setDoc(doc(db, 'notes', id), {
                ...data.note,
                author: user?.email,
                type: 'rider',
                date: moment().format(),
                noteId: ride?.user_id,
                uuid: id,
            })
        }
        yield put(toggleEndRideModal(null))
        // Based on type, get up to date info
        if (data.type === 'active-rides') {
            yield put(getRides())
        }
        if (data.type === 'rider-profile') {
            if (user?.userId) {
                const activeRides: GetActiveRideUserRes = yield call(getActiveRideUser, user?.userId)
                if (activeRides?.data) {
                    yield put(setActiveRideRider(activeRides.data.data))
                } else {
                    yield put(setActiveRideRider(null))
                }

                // set guest rides or clear it
                if (activeRides?.data.guest_rentals) {
                    yield put(setGuestRides(activeRides.data.guest_rentals))
                } else {
                    yield put(setGuestRides(null))
                }
            }
        }
        showNotification('success', 'Successfully ended ride')
    } catch (e) {
        const errors = isAxiosError(e) ? e.response?.data?.errors : null
        if (errors && errors.length > 0) {
            showNotification('error', errors[0].code)
        } else {
            showNotification('error', (e as Error).message)
        }
    }
}

type BlockUserRes = SagaReturnType<typeof blockUser>
type UnblockUserRes = SagaReturnType<typeof unblockUser>
type UnblockUserFMRes = SagaReturnType<typeof unblockUserFM>
type BlockUserFMRes = SagaReturnType<typeof blockUserFM>

export function* block(action: BlockRider) {
    try {
        const { id, blockDesc, blockTypes } = action.payload
        const user: Record<string, any> | null = yield select(selectCurrentRider)
        const isDriversLicenseBlocked = user?.drivingLicenceInfo && user.drivingLicenceInfo.blocked

        // Block cases
        if (blockTypes.user && !user?.userInfo.isBlocked) {
            const res: BlockUserRes = yield call(blockUser, id, blockDesc, 'user')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', 'Successfully blocked user')
        }
        if (blockTypes.payment && !user?.paymentInfo.blocked) {
            const res: BlockUserFMRes = yield call(blockUserFM, id, blockDesc, 'payment')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', "Successfully blocked user's payment")
        }
        if (blockTypes.device && !user?.userInfo.blockedByDevice) {
            const res: BlockUserRes = yield call(blockUser, id, blockDesc, 'device')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', "Successfully blocked user's device")
        }
        if (blockTypes.license && !isDriversLicenseBlocked) {
            const res: BlockUserFMRes = yield call(blockUserFM, id, blockDesc, 'driving_licence')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', "Successfully blocked user's driver's license")
        }

        //Unblock cases
        if (!blockTypes.user && user?.userInfo.isBlocked) {
            const res: UnblockUserRes = yield call(unblockUser, id, 'user')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', 'Successfully unblocked user')
        }
        if (!blockTypes.payment && user?.paymentInfo.blocked) {
            const res: UnblockUserFMRes = yield call(unblockUserFM, id, 'payment')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', "Successfully unblocked user's payment")
        }
        if (!blockTypes.device && user?.userInfo.blockedByDevice) {
            const res: UnblockUserRes = yield call(unblockUser, id, 'device')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', "Successfully unblocked user's device")
        }
        if (!blockTypes.license && isDriversLicenseBlocked) {
            const res: UnblockUserFMRes = yield call(unblockUserFM, id, 'driving_licence')
            if (res instanceof Error) {
                throw res
            }
            yield call(showNotification, 'success', "Successfully unblocked user's driver's license")
        }

        // Needs delay to be able to get new values
        yield delay(1500)
        yield put(getCurrentRiderFullProfile(id))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type DeleteUserRes = SagaReturnType<typeof deleteUser>

export function* deleteRider(action: DeleteRider) {
    const { id, reason, navigateFn } = action.payload
    const res: DeleteUserRes = yield call(deleteUser, id, reason)

    if (res instanceof Error) {
        yield put(setDeleteUserFailed(true))
    } else {
        yield call(showNotification, 'success', 'Successfully deleted rider account')
        // @ts-expect-error https://github.com/redux-saga/redux-saga/issues/1943
        yield call(navigateFn, routes.RIDERS)
    }
}

type RequestIDCheckRes = SagaReturnType<typeof requestUserDocument>

function* requestIDCheck(action: RequestIDCheck) {
    const { reason, userIds } = action.payload
    const res: RequestIDCheckRes = yield call(requestUserDocument, reason, userIds)

    if (res instanceof Error) {
        yield call(showNotification, 'error', res.message)
    } else {
        yield call(showNotification, 'success', 'Successfully request user document')
    }
}

export function* fetchDriversLicenseStatus(action: GetDriversLicenseStatus) {
    const riderId = action.payload
    const driversLicenseStatus: DriversLicenseStatus = yield call(getDriversLicenseStatus, riderId)

    if (driversLicenseStatus instanceof Error) {
        yield call(showNotification, 'error', driversLicenseStatus.message)
        return
    }

    yield put(setDriversLicenseStatus(driversLicenseStatus))
}

export function* deleteRiderDriversLicense(action: DeleteDriversLicense) {
    const riderId = action.payload
    const result: DeleteDriversLicense = yield call(deleteDriversLicense, riderId)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully deleted document')
    yield put(getDriversLicenseStatusAction(riderId))
}

export function* updateDriversLicenseDocumentStatus(action: ApproveDriversLicenseDocument) {
    const riderId = action.payload
    const result: ApproveDriversLicenseDocument = yield call(approveDriversLicenseDocument, riderId)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully approved document')
    yield put(getDriversLicenseStatusAction(riderId))
}

export function* updateDriversLicenseFaceCheckStatus(action: ApproveDriversLicenseFaceCheck) {
    const riderId = action.payload
    const result: ApproveDriversLicenseFaceCheck = yield call(approveDriversLicenseFaceCheck, riderId)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully approved the face check')
    yield put(getDriversLicenseStatusAction(riderId))
}

type AbandonContractRes = SagaReturnType<typeof abandonContract>

export function* abandonRiderContract(action: AbandonContract) {
    const { riderId, contractId } = action.payload
    const result: AbandonContractRes = yield call(abandonContract, riderId, contractId!)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully expired Voi Pass')
    yield put(getRiderContractsAction(riderId))
}

type CancelAndRefundContractRes = SagaReturnType<typeof cancelAndRefundContract>

export function* cancelAndRefundRiderContract(action: CancelAndRefundContract) {
    const { riderId, contractId } = action.payload
    const result: CancelAndRefundContractRes = yield call(cancelAndRefundContract, riderId, contractId!)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully canceled and refunded Voi Pass')
    yield put(getRiderContractsAction(riderId))
}

type StopContractRenewalRes = SagaReturnType<typeof stopContractRenewal>

export function* stopRiderContractRenewal(action: StopContractRenewal) {
    const { riderId, contractId } = action.payload
    const result: StopContractRenewalRes = yield call(stopContractRenewal, riderId, contractId!)

    if (result instanceof Error) {
        yield call(showNotification, 'error', result.message)
        return
    }

    yield call(showNotification, 'success', 'Successfully stopped Voi Pass renewal')
    yield put(getRiderContractsAction(riderId))
}

type GetRiderVoucherHistoryRes = SagaReturnType<typeof getRiderVoucherHistory>

function* fetchRiderVoucherHistory(action: GetRiderVoucherDetails) {
    try {
        const riderId = action.payload
        const result: GetRiderVoucherHistoryRes = yield call(getRiderVoucherHistory, riderId)
        if (result instanceof Error) {
            throw result
        }
        yield put(setRiderVoucherDetailsAction(result))
    } catch (_) {
        yield call(showNotification, 'error', "Couldn't fetch the rider's voucher details")
    }
}

type DeleteRiderVouchersRes = SagaReturnType<typeof deleteRiderVouchersRequest>

function* deleteRiderVouchers(action: DeleteRiderVouchers) {
    try {
        const { voucherIds, riderId } = action.payload
        const res: DeleteRiderVouchersRes = yield call(deleteRiderVouchersRequest, voucherIds)
        if (res instanceof Error) {
            throw res
        }
        yield call(showNotification, 'success', 'Success!')
        yield call(fetchRiderVoucherHistory, getRiderVoucherDetailsAction(riderId))
    } catch (_) {
        yield call(showNotification, 'error', 'Something went wrong')
    }
}

type UnblockDeviceSwitchRes = SagaReturnType<typeof unblockDeviceSwitch>

function* unblockRiderDeviceSwitch(action: UnblockRiderDeviceSwitch) {
    try {
        const { id, reason } = action.payload
        const res: UnblockDeviceSwitchRes = yield call(unblockDeviceSwitch, id, reason)
        if (res instanceof Error) {
            throw res
        }
        yield call(showNotification, 'success', 'Successfully unblocked!')
        yield call(fetchAccountSharingInfo, getAccountSharingInfoAction(id))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type UpdateDateOfBirthRes = SagaReturnType<typeof updateDateOfBirth>

export function* updateRiderDateOfBirth(action: UpdateDateOfBirth) {
    try {
        const { dateOfBirth, riderId } = action.payload
        const res: UpdateDateOfBirthRes = yield call(updateDateOfBirth, riderId, dateOfBirth)
        if (res instanceof Error) {
            throw res
        }
        yield call(showNotification, 'success', 'Successfully updated date of birth')
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type UnlinkUserPhone = SagaReturnType<typeof unlinkUserPhone>

export function* unlinkRiderPhone(action: DeleteRiderPhone) {
    try {
        const res: UnlinkUserPhone = yield call(unlinkUserPhone, action.payload)
        if (res instanceof Error) {
            throw res
        }
        yield call(showNotification, 'success', 'Success!')

        const riderResponse: GetRiderRes = yield call(getRider, action.payload)
        if (riderResponse instanceof Error) {
            throw riderResponse
        }
        // Set current rider
        yield put(setCurrentRider(riderResponse))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type GetRiderFineHistoryById = SagaReturnType<typeof getRiderFineHistoryById>

function* fetchRiderFineHistory(action: GetRiderFineHistory) {
    try {
        const riderId = action.payload
        const result: GetRiderFineHistoryById = yield call(getRiderFineHistoryById, riderId)

        if (result instanceof Error) {
            throw result
        }
        yield put(setRiderFineHistory(result))
    } catch (_) {
        yield call(showNotification, 'error', "Couldn't fetch the rider's fine history")
    }
}

export default function* watcher() {
    yield takeEvery(GET_CURRENT_RIDER_FULL_PROFILE, fetchCurrentRider)
    yield takeEvery(GET_ACCOUNT_SHARING_INFO, fetchAccountSharingInfo)
    yield takeEvery(GET_DISCOUNT_GROUPS, fetchDiscountGroups)
    yield takeEvery(GET_RIDER_DISCOUNT, fetchRiderDiscount)
    yield takeEvery(GET_RIDER_CONTRACTS, fetchRiderContracts)
    yield takeEvery(GET_RIDER_PAYMENT_DETAILS, fetchRiderPaymentDetails)
    yield takeEvery(GET_RIDER_BILLING_PROFILES, fetchRiderBillingProfiles)
    yield takeEvery(GIVE_RIDER_CREDITS, giveCredits)
    yield takeEvery(GIVE_RIDER_DISCOUNTS, giveDiscounts)
    yield takeEvery(FORGIVE_RIDER_DEBT, forgiveDebt)
    yield takeEvery(EDIT_RIDER_DISCOUNT, editRiderDiscount)
    yield takeEvery(GET_RIDER_RIDE_HISTORY, fetchRideHistory)
    yield takeEvery(GET_RIDE_HISTORY_ITEM, fetchRideHistoryItem)
    yield takeEvery(GET_RIDE_PARKING_PHOTO, fetchRideParkingPhoto)
    yield takeEvery(SUBMIT_END_RIDE, endActiveRide)
    yield takeEvery(BLOCK_RIDER, block)
    yield takeEvery(DELETE_RIDER, deleteRider)
    yield takeEvery(GET_DRIVERS_LICENSE_STATUS, fetchDriversLicenseStatus)
    yield takeEvery(DELETE_DRIVERS_LICENSE, deleteRiderDriversLicense)
    yield takeEvery(APPROVE_DRIVERS_LICENSE_DOCUMENT, updateDriversLicenseDocumentStatus)
    yield takeEvery(APPROVE_DRIVERS_LICENSE_FACE_CHECK, updateDriversLicenseFaceCheckStatus)
    yield takeEvery(ABANDON_CONTRACT, abandonRiderContract)
    yield takeEvery(CANCEL_AND_REFUND_CONTRACT, cancelAndRefundRiderContract)
    yield takeEvery(STOP_CONTRACT_RENEWAL, stopRiderContractRenewal)
    yield takeEvery(GET_RIDER_VOUCHER_DETAILS, fetchRiderVoucherHistory)
    yield takeEvery(DELETE_RIDER_VOUCHERS, deleteRiderVouchers)
    yield takeEvery(UNBLOCK_RIDER_DEVICE_SWITCH, unblockRiderDeviceSwitch)
    yield takeEvery(UPDATE_DATE_OF_BIRTH, updateRiderDateOfBirth)
    yield takeEvery(DELETE_RIDER_PHONE, unlinkRiderPhone)
    yield takeEvery(GET_RIDER_FINE_HISTORY, fetchRiderFineHistory)
    yield takeEvery(REQUEST_ID_CHECK, requestIDCheck)
}
